import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import TuneIcon from '@mui/icons-material/Tune';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TableChartIcon from '@mui/icons-material/TableChart';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FactoryIcon from '@mui/icons-material/Factory';
import ExploreIcon from '@mui/icons-material/Explore';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LineWeightIcon from '@mui/icons-material/LineWeight';
import { signedFetch, buildUnauthorizedUrl, isDevLoginSessionLossCode } from '../super-picker/services/api';
import { CardDisplaySettings, CardFieldStyle, CardPosition, PipelineDraftRevision, PipelineItem, PageLayout, PageKey, PAGES, PipelineSavedDraft, PipelineSection } from './types';
import { normalizePipelineTenure } from './utils/projectOrdering';
import { normalizeProjectName } from './utils/displayFormat';
import { fetchDraft, fetchDraftRevisions, fetchItems, fetchLayouts, fetchPipelineAccess, prefetchCandidates, prepareProjectDeckBackgroundForExport, publishResidentialShowcase, restoreDraftRevision, saveDraft } from './services/api';
import { InputTab } from './pages/InputTab';
import { CoverPage } from './pages/CoverPage';
import { OverviewPage } from './pages/OverviewPage';
import { CachedConnectorRoutes, MapPage } from './pages/MapPage';
import { DeckNavigation, MasterControls } from './components/DeckShell';
import { capturePipelinePageJpeg, exportPipelineDeckPdf, jpegBytesToDataUrl as encodeJpegDataUrl } from './utils/pdfExport';

type TabKey = 'input' | 'revisions' | PageKey;
type LoginUser = { Name?: string; Photo?: string };
type EditorSnapshot = {
  overviewItems: PipelineItem[];
  downstreamAdditions: PipelineItem[];
  pageItemOverrides: Record<string, Record<string, Partial<PipelineItem>>>;
  layouts: Record<string, PageLayout>;
};

const PAGE_ICONS: Record<PageKey, React.ReactElement> = {
  cover: <SlideshowIcon fontSize="small" />,
  overview: <TableChartIcon fontSize="small" />,
  residential: <ApartmentIcon fontSize="small" />,
  'commercial-industrial': <FactoryIcon fontSize="small" />,
  'north-north-east': <ExploreIcon fontSize="small" />,
  central: <ExploreIcon fontSize="small" />,
  east: <ExploreIcon fontSize="small" />,
  west: <ExploreIcon fontSize="small" />,
  ec: <HolidayVillageIcon fontSize="small" />,
};

/** Left tab rail: the Input tab first, then one tab per deck page. */
const TABS: Array<{ key: TabKey; label: string; icon: React.ReactElement }> = [
  { key: 'input', label: 'Projects & Data', icon: <TuneIcon fontSize="small" /> },
  ...PAGES.map((p) => ({ key: p.key as TabKey, label: p.label, icon: PAGE_ICONS[p.key] })),
];

// Downstream card placement is generated by MapPage. Version 3 introduced the
// one-time full alignment pass, version 4 raised Residential and EC, and
// version 5 raises Commercial & Industrial without moving the other pages.
const FULL_CARD_ALIGNMENT_VERSION = 3;
const RESIDENTIAL_EC_ALIGNMENT_VERSION = 4;
const CARD_PLACEMENT_VERSION = 5;
const CARD_PLACEMENT_META_KEY = '__card-placement';
// A single reserved layout entry keeps the uploaded downstream artwork from
// being duplicated into every page's layout JSON.
const SHARED_DOWNSTREAM_BACKGROUND_KEY = '__downstream-background';
const SHARED_MAIN_BACKGROUND_KEY = '__main-background';
const CARD_ROUTE_GEOMETRY_FIELDS: Array<keyof CardPosition> = [
  'bendX', 'bendY', 'bend2X', 'bend2Y', 'bend3X', 'bend3Y', 'bend4X', 'bend4Y',
  'bend5X', 'bend5Y', 'bend6X', 'bend6Y', 'bend7X', 'bend7Y',
  'forkX', 'forkY',
];

function migratePlacementLayouts(input: Record<string, PageLayout>): Record<string, PageLayout> {
  // Loading or restoring a draft must never move its cards. Keep the saved
  // placement version intact so handleGenerated can perform the pending
  // one-time alignment only after the user explicitly generates the deck.
  return { ...input };
}

function formatAsAtDate(): string {
  return new Date().toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' });
}

const DEFAULT_CONNECTOR_STROKE_WIDTH = 2;
const MIN_CONNECTOR_STROKE_WIDTH = 1;
const MAX_CONNECTOR_STROKE_WIDTH = 6;

function normalizeConnectorStrokeWidth(value: number, fallback = DEFAULT_CONNECTOR_STROKE_WIDTH): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.round(Math.min(MAX_CONNECTOR_STROKE_WIDTH, Math.max(MIN_CONNECTOR_STROKE_WIDTH, numeric)) * 10) / 10;
}

function formatConnectorStrokeWidth(value: number): string {
  return String(normalizeConnectorStrokeWidth(value));
}

function itemDifference(base: PipelineItem | undefined, edited: Partial<PipelineItem>): Partial<PipelineItem> {
  if (!base) return edited;
  const difference: Partial<PipelineItem> = {};
  (Object.keys(edited) as Array<keyof PipelineItem>).forEach((key) => {
    if (edited[key] !== base[key]) {
      (difference as Record<string, unknown>)[key] = edited[key];
    }
  });
  return difference;
}

function normalizeDraftOverrides(
  overviewItems: PipelineItem[],
  downstreamAdditions: PipelineItem[],
  overrides: Record<string, Record<string, Partial<PipelineItem>>> | undefined,
): Record<string, Record<string, Partial<PipelineItem>>> {
  const baseItems = [...overviewItems, ...downstreamAdditions];
  return Object.fromEntries(
    Object.entries(overrides ?? {}).map(([pageKey, pageOverrides]) => [
      pageKey,
      Object.fromEntries(Object.entries(pageOverrides ?? {}).map(([itemId, override]) => {
        const base = baseItems.find((item) => item.id === Number(itemId));
        const normalized = { ...(override as Record<string, unknown>) };
        const legacyDisplay = typeof normalized.displayName === 'string' ? normalized.displayName : undefined;
        const legacySubtitle = typeof normalized.subTitle === 'string' ? normalized.subTitle : undefined;
        delete normalized.displayName;
        delete normalized.subTitle;
        if (base?.sourceType === 'LANDSALE') {
          if (normalized.roadName == null && (legacySubtitle || legacyDisplay)) normalized.roadName = legacySubtitle || legacyDisplay;
        } else if (normalized.projectName == null && legacyDisplay) {
          normalized.projectName = normalizeProjectName(legacyDisplay);
        }
        if (normalized.projectName !== undefined) {
          normalized.projectName = normalizeProjectName(typeof normalized.projectName === 'string' ? normalized.projectName : null);
          if (normalized.hasProjectName === undefined) normalized.hasProjectName = Boolean(normalized.projectName);
        }
        return [itemId, itemDifference(base, normalized as Partial<PipelineItem>)];
      })),
    ]),
  );
}

/** Convert drafts written before ProjectName/RoadName replaced the old
 * display fields. The legacy keys are handled only at this persistence
 * boundary and are never kept in active editor state. */
function normalizeSavedItems(items: unknown[]): PipelineItem[] {
  return items.map((value) => {
    const raw = (value && typeof value === 'object' ? { ...(value as Record<string, unknown>) } : {}) as Record<string, unknown>;
    const legacyDisplay = typeof raw.displayName === 'string' ? raw.displayName.trim() : '';
    const legacySubtitle = typeof raw.subTitle === 'string' ? raw.subTitle.trim() : '';
    const projectName = normalizeProjectName(typeof raw.projectName === 'string' && raw.projectName.trim()
      ? raw.projectName
      : raw.sourceType === 'LANDSALE' ? null : (legacyDisplay || null));
    const roadName = typeof raw.roadName === 'string' && raw.roadName.trim()
      ? raw.roadName.trim()
      : raw.sourceType === 'LANDSALE' ? (legacySubtitle || legacyDisplay || null) : (legacySubtitle || null);
    delete raw.displayName;
    delete raw.subTitle;
    raw.projectName = projectName;
    raw.roadName = roadName;
    raw.remarks = typeof raw.remarks === 'string' && raw.remarks.trim() ? raw.remarks : null;
    raw.tenure = normalizePipelineTenure(typeof raw.tenure === 'string' ? raw.tenure : null);
    // HasProjectName is the authoritative provenance flag. Preserve an
    // explicit false for road-only cards even when legacy data contains a
    // display value in ProjectName.
    if (typeof raw.hasProjectName !== 'boolean') raw.hasProjectName = Boolean(projectName);
    return raw as unknown as PipelineItem;
  });
}

export function ProjectDeckApp() {
  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>('checking');
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('input');
  const isMobile = useMediaQuery('(max-width:900px)');
  const [items, setItems] = useState<PipelineItem[]>([]);
  const [overviewItems, setOverviewItems] = useState<PipelineItem[]>([]);
  const [downstreamAdditions, setDownstreamAdditions] = useState<PipelineItem[]>([]);
  const [pageItemOverrides, setPageItemOverrides] = useState<Record<string, Record<string, Partial<PipelineItem>>>>({});
  const [layouts, setLayouts] = useState<Record<string, PageLayout>>({});
  const [routingGenerations, setRoutingGenerations] = useState<Partial<Record<PageKey, number>>>({});
  const [connectorRouteCache, setConnectorRouteCache] = useState<Partial<Record<PageKey, CachedConnectorRoutes>>>({});
  const [dataLoading, setDataLoading] = useState(true);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftRevisions, setDraftRevisions] = useState<PipelineDraftRevision[]>([]);
  const [draftRevisionsLoading, setDraftRevisionsLoading] = useState(false);
  const [draftRevisionsError, setDraftRevisionsError] = useState<string | null>(null);
  const [restoringRevisionId, setRestoringRevisionId] = useState<number | null>(null);
  const [restoredDraftPending, setRestoredDraftPending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [cardSettingsOpen, setCardSettingsOpen] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectAllRequests, setSelectAllRequests] = useState<Partial<Record<PageKey, number>>>({});
  const [backgroundUploadPage, setBackgroundUploadPage] = useState<PageKey | null>(null);
  const [connectorThicknessDraft, setConnectorThicknessDraft] = useState(formatConnectorStrokeWidth(DEFAULT_CONNECTOR_STROKE_WIDTH));
  const connectorThicknessEditingRef = useRef(false);
  const connectorThicknessEditingPageRef = useRef<TabKey | null>(null);
  const backgroundInputRef = useRef<HTMLInputElement | null>(null);
  const editorStateRef = useRef<EditorSnapshot>({ overviewItems: [], downstreamAdditions: [], pageItemOverrides: {}, layouts: {} });
  const undoStackRef = useRef<EditorSnapshot[]>([]);
  const redoStackRef = useRef<EditorSnapshot[]>([]);
  const undoGroupActiveRef = useRef(false);
  const undoGroupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // PDF export
  const [exporting, setExporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [exportProgress, setExportProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [exportStage, setExportStage] = useState<'backgrounds' | 'layout' | 'rendering' | 'finalizing'>('rendering');
  const [exportBackgrounds, setExportBackgrounds] = useState<Record<string, string>>({});
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  const exportRoutingReadyRef = useRef<Set<PageKey>>(new Set());
  const exportRoutingWaiterRef = useRef<{
    required: Set<PageKey>;
    resolve: () => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const asAtDate = useMemo(() => formatAsAtDate(), []);

  // Keep the inline thickness field in sync when the active downstream page
  // changes, without replacing text while the user is still typing.
  useEffect(() => {
    const page = PAGES.find((candidate) => candidate.key === activeTab);
    const editingCurrentPage = connectorThicknessEditingRef.current
      && connectorThicknessEditingPageRef.current === activeTab;
    if (!page?.sections || editingCurrentPage) return;
    const width = normalizeConnectorStrokeWidth(
      Number(layouts[page.key]?.['__connector-settings']?.connectorStrokeWidth),
    );
    setConnectorThicknessDraft(formatConnectorStrokeWidth(width));
    connectorThicknessEditingRef.current = false;
    connectorThicknessEditingPageRef.current = null;
  }, [activeTab, layouts]);

  useEffect(() => {
    editorStateRef.current = { overviewItems, downstreamAdditions, pageItemOverrides, layouts };
  }, [downstreamAdditions, layouts, overviewItems, pageItemOverrides]);

  useEffect(() => () => {
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
  }, []);

  const recordUndo = useCallback(() => {
    if (!undoGroupActiveRef.current) {
      undoStackRef.current = [...undoStackRef.current.slice(-49), editorStateRef.current];
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
      undoGroupActiveRef.current = true;
    }
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
    undoGroupTimerRef.current = setTimeout(() => { undoGroupActiveRef.current = false; }, 650);
  }, []);

  const handleUndo = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
    undoGroupActiveRef.current = false;
    redoStackRef.current = [...redoStackRef.current.slice(-49), editorStateRef.current];
    setOverviewItems(previous.overviewItems);
    setDownstreamAdditions(previous.downstreamAdditions);
    setPageItemOverrides(previous.pageItemOverrides);
    setLayouts(previous.layouts);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    setToast('Last change undone.');
  }, []);

  const handleRedo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
    undoGroupActiveRef.current = false;
    undoStackRef.current = [...undoStackRef.current.slice(-49), editorStateRef.current];
    setOverviewItems(next.overviewItems);
    setDownstreamAdditions(next.downstreamAdditions);
    setPageItemOverrides(next.pageItemOverrides);
    setLayouts(next.layouts);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    setToast('Last change redone.');
  }, []);

  const applySavedDraft = useCallback((draft: PipelineSavedDraft) => {
    if (
      !draft
      || draft.version !== 1
      || !Array.isArray(draft.overviewItems)
      || !Array.isArray(draft.downstreamAdditions)
      || !draft.layouts
      || typeof draft.layouts !== 'object'
    ) {
      throw new Error('The selected revision contains an invalid draft snapshot.');
    }
    const restoredOverview = normalizeSavedItems(draft.overviewItems ?? []);
    const restoredAdditions = normalizeSavedItems(draft.downstreamAdditions ?? []);
    setOverviewItems(restoredOverview);
    setDownstreamAdditions(restoredAdditions);
    setPageItemOverrides(normalizeDraftOverrides(restoredOverview, restoredAdditions, draft.pageItemOverrides));
    setLayouts(migratePlacementLayouts(draft.layouts ?? {}));
    setConnectorRouteCache({});
    setRoutingGenerations((current) => Object.fromEntries(
      PAGES.filter((page) => page.sections).map((page) => [page.key, (current[page.key] ?? 0) + 1]),
    ));
    setCardSettingsOpen(false);
    setMultiSelectMode(false);
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
    undoGroupActiveRef.current = false;
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  const loadDraftRevisions = useCallback(async () => {
    setDraftRevisionsLoading(true);
    setDraftRevisionsError(null);
    try {
      setDraftRevisions(await fetchDraftRevisions());
    } catch (error) {
      setDraftRevisionsError(error instanceof Error ? error.message : 'Failed to load revisions');
    } finally {
      setDraftRevisionsLoading(false);
    }
  }, []);

  const handleRestoreRevision = useCallback(async (revision: PipelineDraftRevision) => {
    if (!window.confirm('Load this revision into the editor? You can save it later as the current draft.')) return;
    setRestoringRevisionId(revision.id);
    try {
      const restored = await restoreDraftRevision(revision.id);
      applySavedDraft(restored);
      setRestoredDraftPending(true);
      setActiveTab('overview');
      await loadDraftRevisions();
      setToast('Revision loaded. Save current draft to apply it.');
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Failed to restore draft revision');
    } finally {
      setRestoringRevisionId(null);
    }
  }, [applySavedDraft, loadDraftRevisions]);

  useEffect(() => {
    let mounted = true;
    const accessStartedAt = performance.now();
    (async () => {
      try {
        const res = await signedFetch('/api/users/me');
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const code = typeof data?.code === 'string' ? data.code : undefined;
          if (mounted) {
            setAccessState('denied');
            if (!(res.status === 401 && isDevLoginSessionLossCode(code))) {
              window.location.replace(buildUnauthorizedUrl(code, { app: 'super-picker' }));
            }
          }
          return;
        }
        // Project Deck gate: only TransformationAppUserAccess App='PROJECT-DECK'
        // Role='ADMIN' holders may use Super Pipeline.
        const allowed = await fetchPipelineAccess().catch(() => false);
        if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_SUPER_PIPELINE_TIMING === 'true') {
          console.debug('[project-deck] access check', { elapsedMs: Math.round(performance.now() - accessStartedAt), allowed });
        }
        if (mounted) {
          if (!allowed) {
            setAccessState('denied');
            window.location.replace(
              buildUnauthorizedUrl('NO_PROJECT_DECK_ACCESS', { app: 'super-picker' }),
            );
            return;
          }
          const user = data?.user ?? {};
          setLoginUser({ Name: user.Name ?? user.name, Photo: user.Photo ?? user.photo });
          setAccessState('granted');
          // Warm the heaviest call so the Input tab renders instantly.
          prefetchCandidates();
        }
      } catch {
        if (mounted) setAccessState('denied');
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (accessState !== 'granted') return;
    let mounted = true;
    const startedAt = performance.now();
    (async () => {
      try {
        const [savedItems, savedLayouts, savedDraft] = await Promise.all([fetchItems(), fetchLayouts(), fetchDraft()]);
        if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_SUPER_PIPELINE_TIMING === 'true') {
          console.debug('[project-deck] saved deck load', { elapsedMs: Math.round(performance.now() - startedAt) });
        }
        if (!mounted) return;
        setItems(savedItems);
        const restoredOverview = savedDraft?.version === 1
          ? normalizeSavedItems(savedDraft.overviewItems)
          : savedItems;
        const restoredAdditions = savedDraft?.version === 1
          ? normalizeSavedItems(savedDraft.downstreamAdditions ?? [])
          : [];
        setOverviewItems(restoredOverview);
        setDownstreamAdditions(restoredAdditions);
        const restoredOverrides = savedDraft?.version === 1 ? savedDraft.pageItemOverrides ?? {} : {};
        setPageItemOverrides(normalizeDraftOverrides(restoredOverview, restoredAdditions, restoredOverrides));
        const clean: Record<string, PageLayout> = {};
        for (const [k, v] of Object.entries(savedLayouts)) {
          if (v && typeof v === 'object') clean[k] = v as PageLayout;
        }
        const restoredLayouts = savedDraft?.version === 1 ? savedDraft.layouts : clean;
        setLayouts(migratePlacementLayouts(restoredLayouts));
        // Returning users with a generated deck land on the overview.
        if (savedItems.length > 0) setActiveTab('overview');
      } catch {
        if (mounted) setToast('Could not load the saved deck — starting fresh.');
      } finally {
        if (mounted) setDataLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [accessState]);

  useEffect(() => {
    if (accessState === 'granted' && activeTab === 'revisions') loadDraftRevisions();
  }, [accessState, activeTab, loadDraftRevisions]);

  const moveCard = useCallback((pageKey: string, itemId: number | string, xPct: number, yPct: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [String(itemId)]: { ...(prev[pageKey]?.[String(itemId)] || {}), x: xPct, y: yPct },
      },
    }));
  }, [recordUndo]);

  const resizeCard = useCallback((pageKey: string, itemId: number | string, widthPct: number, heightPct?: number, xPct?: number, yPct?: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [String(itemId)]: {
          ...(prev[pageKey]?.[String(itemId)] || {}),
          width: widthPct,
          ...(heightPct == null ? {} : { height: heightPct }),
          ...(xPct == null ? {} : { x: xPct }),
          ...(yPct == null ? {} : { y: yPct }),
        },
      },
    }));
  }, [recordUndo]);

  const resizeTableColumns = useCallback((_sectionKey: string, columnWidths: Record<string, number>) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        // Overview tables intentionally share one width map. Keep the
        // sectionKey parameter for callback compatibility with older pages,
        // but never persist widths under individual section entries.
        columnWidths,
      },
    }));
  }, [recordUndo]);

  const setTableAspectLocked = useCallback((sectionKey: string, aspectLocked: boolean) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        [sectionKey]: { ...(prev.overview?.[sectionKey] || {}), aspectLocked },
      },
    }));
  }, [recordUndo]);

  const bendLine = useCallback((pageKey: string, itemId: number, bendX: number, bendY: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [String(itemId)]: {
          ...(prev[pageKey]?.[String(itemId)] || {}), bendX, bendY,
        },
      },
    }));
  }, [recordUndo]);

  const updateLineLayout = useCallback((pageKey: string, itemId: number, patch: Partial<CardPosition>) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [String(itemId)]: { ...(prev[pageKey]?.[String(itemId)] || {}), ...patch },
      },
    }));
  }, [recordUndo]);

  const updateRegionColor = useCallback((section: PipelineSection, color?: string) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        ...(section === 'NORTH' || section === 'NORTH_EAST'
          ? {
            'color:NORTH_GROUP': { ...(prev.overview?.['color:NORTH_GROUP'] || {}), color },
            'color:NORTH': { ...(prev.overview?.['color:NORTH'] || {}), color },
            'color:NORTH_EAST': { ...(prev.overview?.['color:NORTH_EAST'] || {}), color },
          }
          : { [`color:${section}`]: { ...(prev.overview?.[`color:${section}`] || {}), color } }),
      },
    }));
  }, [recordUndo]);

  const adjustLegend = useCallback((pageKey: string, x: number, y: number, width: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        legend: { ...(prev[pageKey]?.legend || {}), x, y, width },
      },
    }));
  }, [recordUndo]);

  const updateConnectorStrokeWidth = useCallback((pageKey: PageKey, width: number) => {
    const nextWidth = normalizeConnectorStrokeWidth(width);
    const currentWidth = normalizeConnectorStrokeWidth(
      Number(layouts[pageKey]?.['__connector-settings']?.connectorStrokeWidth),
    );
    if (currentWidth === nextWidth) return;
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        '__connector-settings': {
          ...(prev[pageKey]?.['__connector-settings'] || {}),
          connectorStrokeWidth: nextWidth,
        },
      },
    }));
  }, [layouts, recordUndo]);

  const updateLaunchingSoonColor = useCallback((color?: string) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        legend: { ...(prev.overview?.legend || {}), launchingSoonColor: color },
      },
    }));
  }, [recordUndo]);

  const updateLaunchingSoonText = useCallback((text?: string) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        legend: { ...(prev.overview?.legend || {}), launchingSoonText: text?.trim() || undefined },
      },
    }));
  }, [recordUndo]);

  const updateLegendCaption = useCallback((x: number, y: number, width?: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        'legend-caption': {
          ...(prev.overview?.['legend-caption'] || {}),
          x,
          y,
          ...(width != null ? { width } : {}),
        },
      },
    }));
  }, [recordUndo]);

  const addLaunchingSoonBox = useCallback(() => {
    const overviewLayout = layouts.overview || {};
    const existingKeys = Object.keys(overviewLayout).filter((key) => key === 'launching-soon-box' || /^launching-soon-box:\d+$/.test(key));
    const key = !overviewLayout['launching-soon-box']
      ? 'launching-soon-box'
      : (() => {
        let index = 1;
        while (overviewLayout[`launching-soon-box:${index}`]) index += 1;
        return `launching-soon-box:${index}`;
      })();
    const offset = existingKeys.length * 3;
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        [key]: {
          x: Math.min(73 + offset, 84),
          y: Math.min(61 + offset, 84),
          width: 14,
          height: 14,
        },
      },
    }));
    setToast('Launching Soon box added. Drag its border or resize handle to position it.');
  }, [layouts.overview, recordUndo]);

  const updateLaunchingSoonBox = useCallback((boxKey: string, x: number, y: number, width: number, height: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      overview: {
        ...(prev.overview || {}),
        [boxKey]: {
          ...(prev.overview?.[boxKey] || {}),
          x, y, width, height,
        },
      },
    }));
  }, [recordUndo]);

  const deleteLaunchingSoonBox = useCallback((boxKey: string) => {
    if (!layouts.overview?.[boxKey]) return;
    recordUndo();
    setLayouts((prev) => {
      const overview = { ...(prev.overview || {}) };
      delete overview[boxKey];
      return { ...prev, overview };
    });
    setToast('Launching Soon box deleted.');
  }, [layouts.overview, recordUndo]);

  const uploadBackground = useCallback((pageKey: PageKey, file: File) => {
    if (!file.type.startsWith('image/')) {
      setToast('Choose an image file for the page background.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = typeof reader.result === 'string' ? reader.result : null;
      if (!image) return;
      recordUndo();
      const isDownstream = PAGES.some((page) => page.key === pageKey && page.sections);
      const isMain = pageKey === 'cover' || pageKey === 'overview';
      setLayouts((prev) => isDownstream
        ? {
          ...prev,
          [SHARED_DOWNSTREAM_BACKGROUND_KEY]: {
            ...(prev[SHARED_DOWNSTREAM_BACKGROUND_KEY] || {}),
            '__background': {
              ...(prev[SHARED_DOWNSTREAM_BACKGROUND_KEY]?.['__background'] || {}),
              backgroundImage: image,
            },
          },
        }
        : isMain
        ? {
          ...prev,
          [SHARED_MAIN_BACKGROUND_KEY]: {
            ...(prev[SHARED_MAIN_BACKGROUND_KEY] || {}),
            '__background': {
              ...(prev[SHARED_MAIN_BACKGROUND_KEY]?.['__background'] || {}),
              backgroundImage: image,
            },
          },
        }
        : {
          ...prev,
          [pageKey]: {
            ...(prev[pageKey] || {}),
            '__background': { ...(prev[pageKey]?.['__background'] || {}), backgroundImage: image },
          },
        });
      setToast(isDownstream
        ? 'Subsequent-tab background updated.'
        : isMain
        ? 'Cover and Overview background updated.'
        : `${PAGES.find((page) => page.key === pageKey)?.label ?? 'Page'} background updated.`);
    };
    reader.onerror = () => setToast('Could not read that image.');
    reader.readAsDataURL(file);
  }, [recordUndo]);

  const updateCardDisplay = useCallback((pageKey: string, cardDisplay: CardDisplaySettings, targetItemIds: number[]) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        ...Object.fromEntries(targetItemIds.map((itemId) => {
          const key = String(itemId);
          const current = prev[pageKey]?.[key] || {};
          const currentFields = current.cardDisplay?.fields || {};
          const patchFields = cardDisplay.fields || {};
          const nextFields: Record<string, CardFieldStyle> = {};
          const fieldKeys = new Set([...Object.keys(currentFields), ...Object.keys(patchFields)]);
          fieldKeys.forEach((fieldKey) => {
            const currentStyle = currentFields[fieldKey as keyof typeof currentFields] || {};
            const patchStyle = patchFields[fieldKey as keyof typeof patchFields] || {};
            const nextStyle = { ...currentStyle, ...patchStyle } as Record<string, unknown>;
            Object.keys(nextStyle).forEach((styleKey) => {
              if (nextStyle[styleKey] === undefined) delete nextStyle[styleKey];
            });
            if (Object.keys(nextStyle).length) nextFields[fieldKey] = nextStyle;
          });
          const nextCard = { ...current };
          if (Object.keys(nextFields).length) nextCard.cardDisplay = { ...(current.cardDisplay || {}), fields: nextFields };
          else delete nextCard.cardDisplay;
          return [key, nextCard];
        })),
      },
    }));
  }, [recordUndo]);

  const updatePageItem = useCallback((pageKey: PageKey, updated: PipelineItem) => {
    recordUndo();
    const base = [...overviewItems, ...downstreamAdditions].find((item) => item.id === updated.id);
    const difference = itemDifference(base, updated);
    setPageItemOverrides((current) => ({
      ...current,
      [pageKey]: {
        ...(current[pageKey] || {}),
        [String(updated.id)]: difference,
      },
    }));
  }, [downstreamAdditions, overviewItems, recordUndo]);

  const handleGenerated = useCallback((saved: PipelineItem[]) => {
    setRestoredDraftPending(false);
    // Generation establishes a new editing baseline. Do not let Undo restore
    // the pre-generation/blank canvas; history begins with the first user
    // movement, bend, or other edit after this point.
    if (undoGroupTimerRef.current) clearTimeout(undoGroupTimerRef.current);
    undoGroupActiveRef.current = false;
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    const savedIds = new Set(saved.map((item) => String(item.id)));
    const preservedLayouts: Record<string, PageLayout> = { ...layouts };
    // Keep furniture and user-authored geometry, but discard card entries for
    // projects that are no longer part of the generated deck. Placement
    // migrations perform one alignment pass for the affected pages: only their
    // positions and now-stale connector route points are cleared. Card
    // dimensions and visual settings remain authoritative. After MapPage
    // persists the aligned x/y values, future generations retain them and
    // allocate only new projects.
    PAGES.filter((page) => page.sections).forEach((page) => {
      const existing = layouts[page.key] || {};
      const placementVersion = existing[CARD_PLACEMENT_META_KEY]?.placementVersion ?? 0;
      const shouldApplyFullAlignment = placementVersion < FULL_CARD_ALIGNMENT_VERSION;
      const shouldApplyResidentialEcAlignment = (page.key === 'residential' || page.key === 'ec')
        && placementVersion < RESIDENTIAL_EC_ALIGNMENT_VERSION;
      const shouldApplyCommercialAlignment = page.key === 'commercial-industrial'
        && placementVersion < CARD_PLACEMENT_VERSION;
      const shouldAlignRetainedCards = shouldApplyFullAlignment
        || shouldApplyResidentialEcAlignment
        || shouldApplyCommercialAlignment;
      const retainedEntries = Object.entries(existing)
        .filter(([key]) => !/^[-]?\d+$/.test(key) || savedIds.has(key))
        .map(([key, value]) => {
          if (!shouldAlignRetainedCards || !/^[-]?\d+$/.test(key)) return [key, value];
          const alignedCard = { ...value };
          delete alignedCard.x;
          delete alignedCard.y;
          CARD_ROUTE_GEOMETRY_FIELDS.forEach((field) => { delete alignedCard[field]; });
          return [key, alignedCard];
        });
      preservedLayouts[page.key] = {
        ...Object.fromEntries(retainedEntries),
        [CARD_PLACEMENT_META_KEY]: {
          ...(existing[CARD_PLACEMENT_META_KEY] || {}),
          placementVersion: CARD_PLACEMENT_VERSION,
        },
      };
    });
    const preservedOverrides = Object.fromEntries(
      Object.entries(pageItemOverrides).map(([pageKey, overrides]) => [
        pageKey,
        Object.fromEntries(Object.entries(overrides).filter(([itemId]) => savedIds.has(itemId))),
      ]),
    );
    setItems(saved);
    setOverviewItems(saved);
    setDownstreamAdditions([]);
    setPageItemOverrides(preservedOverrides);
    setLayouts(preservedLayouts);
    setConnectorRouteCache({});
    // Bump every downstream placement generation so hidden preloaders discard
    // old slots/routes even when the regenerated projects retain the same IDs.
    setRoutingGenerations((current) => Object.fromEntries(
      PAGES.filter((page) => page.sections).map((page) => [page.key, (current[page.key] ?? 0) + 1]),
    ));
    setActiveTab('overview');
    setToast('Deck generated — review and edit the overview, then save your draft.');
  }, [layouts, pageItemOverrides]);

  const handleSaveDraft = useCallback(async () => {
    setDraftSaving(true);
    try {
      const draft: PipelineSavedDraft = { version: 1, overviewItems, downstreamAdditions, layouts, pageItemOverrides };
      const savedDraft = await saveDraft(draft);
      // Keep local state aligned with the persisted draft so newly uploaded
      // Firebase background URLs are reused on subsequent saves.
      setLayouts(savedDraft.layouts);
      setRestoredDraftPending(false);
      await loadDraftRevisions();
      setToast('Current draft saved. It will be restored when you return.');
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Failed to save current draft');
    } finally {
      setDraftSaving(false);
    }
  }, [downstreamAdditions, layouts, overviewItems, pageItemOverrides, loadDraftRevisions]);

  const updateOverviewItem = useCallback((updated: PipelineItem) => {
    recordUndo();
    setOverviewItems((current) => current.map((item) => item.id === updated.id ? updated : item));
  }, [recordUndo]);

  const moveMarker = useCallback((pageKey: PageKey, markerKey: string, xPct: number, yPct: number) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [markerKey]: {
          ...(prev[pageKey]?.[markerKey] || {}),
          x: Math.max(0, Math.min(100, xPct)),
          y: Math.max(0, Math.min(100, yPct)),
        },
      },
    }));
  }, [recordUndo]);

  const updateMarkerLineLayout = useCallback((pageKey: PageKey, markerKey: string, patch: Partial<CardPosition>) => {
    recordUndo();
    setLayouts((prev) => ({
      ...prev,
      [pageKey]: {
        ...(prev[pageKey] || {}),
        [markerKey]: { ...(prev[pageKey]?.[markerKey] || {}), ...patch },
      },
    }));
  }, [recordUndo]);

  const updateMarkerBend = useCallback((pageKey: PageKey, markerKey: string, bendIndex: number, xPct: number, yPct: number) => {
    const xKey = bendIndex === 0 ? 'markerBendX' : `markerBend${bendIndex + 1}X`;
    const yKey = bendIndex === 0 ? 'markerBendY' : `markerBend${bendIndex + 1}Y`;
    updateMarkerLineLayout(pageKey, markerKey, { [xKey]: xPct, [yKey]: yPct } as Partial<CardPosition>);
  }, [updateMarkerLineLayout]);

  const persistGeneratedCardPositions = useCallback((pageKey: PageKey, positions: Record<string, { x: number; y: number }>) => {
    if (!Object.keys(positions).length) return;
    setLayouts((previous) => {
      const currentPage = previous[pageKey] || {};
      let changed = false;
      const nextPage: PageLayout = { ...currentPage };
      Object.entries(positions).forEach(([itemId, position]) => {
        const current = currentPage[itemId];
        if (current?.x != null || current?.y != null) return;
        nextPage[itemId] = { ...(current || {}), x: position.x, y: position.y };
        changed = true;
      });
      return changed ? { ...previous, [pageKey]: nextPage } : previous;
    });
  }, []);

  // ── PDF export ──
  const handleExport = useCallback(async () => {
    if (items.length === 0) {
      setToast('Generate the deck first — there is nothing to export yet.');
      return;
    }
    setExporting(true);
    setExportStage('backgrounds');
    setExportProgress({ done: 0, total: 0 });
    // Let the progress dialog paint before any Firebase/background work starts.
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    const requiredPages = new Set(PAGES.filter((page) => page.sections).map((page) => page.key));
    const mainBackground = layouts[SHARED_MAIN_BACKGROUND_KEY]?.['__background']?.backgroundImage;
    const downstreamBackground = layouts[SHARED_DOWNSTREAM_BACKGROUND_KEY]?.['__background']?.backgroundImage;
    const preparedBackgrounds: Record<string, string> = {};
    const temporaryUrls: string[] = [];
    try {
      const backgroundSources = PAGES.reduce<Record<string, string>>((sources, page) => {
        const source = page.key === 'cover' || page.key === 'overview'
          ? mainBackground || layouts[page.key]?.['__background']?.backgroundImage
          : downstreamBackground || layouts[page.key]?.['__background']?.backgroundImage;
        if (source) sources[page.key] = source;
        return sources;
      }, {});
      const uniqueSources = Array.from(new Set(Object.values(backgroundSources)));
      setExportProgress({ done: 0, total: uniqueSources.length });
      const preparedBySource = new Map<string, string>();
      await Promise.all(uniqueSources.map(async (source) => {
        const prepared = await prepareProjectDeckBackgroundForExport(source);
        preparedBySource.set(source, prepared);
        if (prepared !== source && prepared.startsWith('blob:')) temporaryUrls.push(prepared);
        setExportProgress((current) => ({ ...current, done: current.done + 1 }));
      }));
      Object.entries(backgroundSources).forEach(([pageKey, source]) => {
        preparedBackgrounds[pageKey] = preparedBySource.get(source) || source;
      });
    } catch (error) {
      temporaryUrls.forEach((url) => URL.revokeObjectURL(url));
      setExporting(false);
      setExportBackgrounds({});
      setToast(error instanceof Error ? error.message : 'Could not prepare background images for PDF export');
      return;
    }
    setExportBackgrounds(preparedBackgrounds);
    setExportStage('layout');
    setExportProgress({ done: 0, total: PAGES.length });
    exportRoutingReadyRef.current = new Set();
    const routingReady = new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        const missing = Array.from(requiredPages).filter((key) => !exportRoutingReadyRef.current.has(key));
        if (missing.length && process.env.REACT_APP_DEBUG_PIPELINE_PDF === 'true') {
          console.warn('[pipeline-pdf] connector readiness timed out; using deterministic fallback geometry', { missing });
        }
        resolve();
      }, 5000);
      exportRoutingWaiterRef.current = { required: requiredPages, resolve, timer };
    });
    await new Promise((resolve) => setTimeout(resolve, 60));
    try {
      await routingReady;
      if (exportRoutingWaiterRef.current) {
        clearTimeout(exportRoutingWaiterRef.current.timer);
        exportRoutingWaiterRef.current = null;
      }
      const root = exportRootRef.current;
      if (!root) throw new Error('Export renderer not ready');
      setExportStage('rendering');
      await exportPipelineDeckPdf(root, (done, total) => setExportProgress({ done, total }));
      setExportStage('finalizing');
      setToast('PDF downloaded.');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'PDF export failed');
    } finally {
      if (exportRoutingWaiterRef.current) {
        clearTimeout(exportRoutingWaiterRef.current.timer);
        exportRoutingWaiterRef.current = null;
      }
      setExporting(false);
      temporaryUrls.forEach((url) => URL.revokeObjectURL(url));
      setExportBackgrounds({});
    }
  }, [items.length, layouts]);

  const handlePublish = useCallback(async () => {
    if (items.length === 0) {
      setToast('Generate the deck first — there is nothing to publish yet.');
      return;
    }
    if (publishing || exporting) return;

    setPublishing(true);
    setPublishProgress(0);
    const temporaryUrls: string[] = [];
    try {
      // Prepare the shared downstream background in the same way as PDF
      // export. The print tree is then captured, rather than the scaled UI.
      const source = layouts[SHARED_DOWNSTREAM_BACKGROUND_KEY]?.['__background']?.backgroundImage
        || layouts.residential?.['__background']?.backgroundImage;
      const preparedBackgrounds: Record<string, string> = {};
      if (source) {
        const prepared = await prepareProjectDeckBackgroundForExport(source);
        preparedBackgrounds.residential = prepared;
        if (prepared !== source && prepared.startsWith('blob:')) temporaryUrls.push(prepared);
      }
      setPublishProgress(15);
      setExportBackgrounds(preparedBackgrounds);
      exportRoutingReadyRef.current = new Set();
      const requiredPages = new Set<PageKey>(['residential']);
      const routingReady = new Promise<void>((resolve) => {
        const timer = setTimeout(() => resolve(), 5000);
        exportRoutingWaiterRef.current = { required: requiredPages, resolve, timer };
      });

      // Allow the hidden fixed tree and its connector routes to mount before
      // waiting for readiness.
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      await new Promise<void>((resolve) => setTimeout(resolve, 60));
      setPublishProgress(30);
      await routingReady;
      if (exportRoutingWaiterRef.current) {
        clearTimeout(exportRoutingWaiterRef.current.timer);
        exportRoutingWaiterRef.current = null;
      }
      const root = exportRootRef.current;
      if (!root) throw new Error('Residential export renderer not ready.');
      const captured = await capturePipelinePageJpeg(
        root,
        'residential',
        (stage) => {
            setPublishProgress(stage === 'preparing' ? 35 : stage === 'capturing' ? 55 : stage === 'encoding' ? 75 : 80);
        },
      );
      setPublishProgress(85);
      const item = await publishResidentialShowcase({
        imageDataUrl: encodeJpegDataUrl(captured.bytes),
        imageWidth: captured.width,
        imageHeight: captured.height,
      });
      setPublishProgress(100);
      setToast(
        item.notification?.sent
          ? `${item.title} showcase draft created. Reviewer notification sent.`
          : `${item.title} showcase draft created, but the reviewer notification email was not sent.`,
      );
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Could not capture the Residential page.');
    } finally {
      if (exportRoutingWaiterRef.current) {
        clearTimeout(exportRoutingWaiterRef.current.timer);
        exportRoutingWaiterRef.current = null;
      }
      temporaryUrls.forEach((url) => URL.revokeObjectURL(url));
      setExportBackgrounds({});
      setPublishing(false);
      setPublishProgress(0);
    }
  }, [exporting, items.length, layouts, publishing]);

  const handleRoutingComplete = useCallback((pageKey: PageKey, routes: CachedConnectorRoutes) => {
    setConnectorRouteCache((current) => {
      const previous = current[pageKey];
      if (previous === routes
        || (previous?.generation === routes.generation
          && previous.identity === routes.identity
          && previous.complete === routes.complete)) {
        return current;
      }
      return { ...current, [pageKey]: routes };
    });
    if (!routes.complete) return;
    exportRoutingReadyRef.current.add(pageKey);
    const waiter = exportRoutingWaiterRef.current;
    if (!waiter) return;
    if (Array.from(waiter.required).every((key) => exportRoutingReadyRef.current.has(key))) {
      clearTimeout(waiter.timer);
      waiter.resolve();
      exportRoutingWaiterRef.current = null;
    }
  }, []);

  if (accessState === 'checking') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Checking Super Pipeline access…</Typography>
      </Box>
    );
  }
  if (accessState === 'denied') return null;

  const activePage = PAGES.find((p) => p.key === activeTab) || null;
  const sharedMainBackground = layouts[SHARED_MAIN_BACKGROUND_KEY]?.['__background']?.backgroundImage;
  const sharedDownstreamBackground = layouts[SHARED_DOWNSTREAM_BACKGROUND_KEY]?.['__background']?.backgroundImage;
  const backgroundForPage = (pageKey: PageKey, fallback?: string) => {
    const page = PAGES.find((candidate) => candidate.key === pageKey);
    if (pageKey === 'cover' || pageKey === 'overview') {
      return sharedMainBackground
        || layouts.cover?.['__background']?.backgroundImage
        || layouts.overview?.['__background']?.backgroundImage
        || fallback;
    }
    if (page?.sections) {
      return sharedDownstreamBackground || layouts[pageKey]?.['__background']?.backgroundImage || fallback;
    }
    return layouts[pageKey]?.['__background']?.backgroundImage || fallback;
  };
  const backgroundForExportPage = (pageKey: PageKey, fallback?: string) => {
    const source = backgroundForPage(pageKey, fallback);
    return exportBackgrounds[pageKey] || source;
  };
  const activeConnectorThickness = activePage?.sections
    ? normalizeConnectorStrokeWidth(Number(layouts[activePage.key]?.['__connector-settings']?.connectorStrokeWidth))
    : DEFAULT_CONNECTOR_STROKE_WIDTH;
  const commitConnectorThickness = (raw: string) => {
    connectorThicknessEditingRef.current = false;
    connectorThicknessEditingPageRef.current = null;
    const parsed = raw.trim() ? Number(raw) : Number.NaN;
    if (!Number.isFinite(parsed)) {
      setConnectorThicknessDraft(formatConnectorStrokeWidth(activeConnectorThickness));
      return;
    }
    const next = normalizeConnectorStrokeWidth(parsed);
    setConnectorThicknessDraft(formatConnectorStrokeWidth(next));
    if (activePage?.sections) updateConnectorStrokeWidth(activePage.key, next);
  };
  const adjustConnectorThickness = (delta: number) => {
    connectorThicknessEditingRef.current = false;
    connectorThicknessEditingPageRef.current = null;
    const current = connectorThicknessDraft.trim() ? Number(connectorThicknessDraft) : Number.NaN;
    const base = Number.isFinite(current) ? current : activeConnectorThickness;
    const next = normalizeConnectorStrokeWidth(base + delta);
    setConnectorThicknessDraft(formatConnectorStrokeWidth(next));
    if (activePage?.sections) updateConnectorStrokeWidth(activePage.key, next);
  };
  const parsedConnectorThicknessDraft = connectorThicknessDraft.trim() ? Number(connectorThicknessDraft) : Number.NaN;
  const connectorThicknessForButtons = Number.isFinite(parsedConnectorThicknessDraft)
    ? normalizeConnectorStrokeWidth(parsedConnectorThicknessDraft)
    : activeConnectorThickness;

  return (
    <Box sx={{ height: '100vh', bgcolor: 'var(--color-bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DeckNavigation title="Projects Pipeline" loginUser={loginUser} />
      {/* Body: fixed left tab rail + content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          mt: '64px',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              width: { xs: '100%', md: 240 },
              flexShrink: 0,
              bgcolor: 'var(--color-surface)',
              borderRight: { md: '1px solid rgba(var(--color-text-rgb), 0.08)' },
              borderBottom: { xs: '1px solid rgba(var(--color-text-rgb), 0.08)', md: 'none' },
              overflowY: { md: 'auto' },
            }}
          >
          <Tabs
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant="scrollable"
            scrollButtons="auto"
            value={activeTab === 'revisions' ? false : activeTab}
            onChange={(_, v) => {
              setCardSettingsOpen(false);
              setMultiSelectMode(false);
              setActiveTab(v as TabKey);
            }}
            sx={{
              py: { md: 1 },
              '& .MuiTabs-indicator': isMobile
                ? {}
                : { left: 0, right: 'auto', width: 3, borderRadius: 2 },
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                justifyContent: 'flex-start',
                gap: 1,
                px: 2,
                color: 'var(--color-text-muted)',
                '&.Mui-selected': { color: 'var(--color-accent)' },
              },
            }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={t.label}
                icon={t.icon}
                iconPosition="start"
                sx={
                  t.key === 'cover' && !isMobile
                    ? { borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', mt: 0.5 }
                    : undefined
                }
              />
            ))}
          </Tabs>
          </Box>
          <Box
          component="main"
          sx={{
            flexGrow: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'var(--color-bg)',
            minWidth: 0, width: '100%', height: '100%', overflowY: 'auto', overflowX: 'hidden',
            color: 'var(--color-text-strong)',
          }}
        >
          <MasterControls
            canUndo={canUndo}
            canRedo={canRedo}
            draftSaving={draftSaving}
            exporting={exporting}
            publishing={publishing}
            hasItems={items.length > 0}
            revisionsActive={activeTab === 'revisions'}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onRevisions={() => setActiveTab('revisions')}
            onSaveDraft={handleSaveDraft}
            onExport={handleExport}
            onPublish={handlePublish}
          />
          {dataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : activeTab === 'input' ? (
            <InputTab items={items} onGenerated={handleGenerated} />
          ) : activeTab === 'revisions' ? (
            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>Draft revisions</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                    Shared history for the current Super Pipeline deck.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={loadDraftRevisions} disabled={draftRevisionsLoading}>
                  Refresh
                </Button>
              </Box>
              {restoredDraftPending && <Alert severity="info" sx={{ mb: 2 }}>Revision loaded. Save current draft to apply it.</Alert>}
              {draftRevisionsError && <Alert severity="error" sx={{ mb: 2 }}>{draftRevisionsError}</Alert>}
              {draftRevisionsLoading ? <CircularProgress /> : draftRevisions.length === 0 ? (
                <Typography sx={{ color: 'var(--color-text-muted)' }}>No saved draft revisions yet.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {draftRevisions.map((revision) => (
                    <Paper key={revision.id} sx={{ p: 2, bgcolor: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={800}>{revision.defunctAt == null ? 'Current draft' : 'Historical revision'}</Typography>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                          Saved {revision.updatedAt || revision.createdAt ? new Date(revision.updatedAt || revision.createdAt || '').toLocaleString() : '—'}
                          {` by ${revision.updatedByName || 'Unknown user'}`}
                        </Typography>
                        {revision.defunctAt && <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Replaced {new Date(revision.defunctAt).toLocaleString()}</Typography>}
                        <Typography variant="caption" display="block" sx={{ color: 'var(--color-text-muted)' }}>
                          {revision.draft.overviewItems?.length ?? 0} projects · {Object.keys(revision.draft.layouts ?? {}).length} layout pages
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={revision.defunctAt == null || restoringRevisionId === revision.id}
                        onClick={() => handleRestoreRevision(revision)}
                      >
                        {restoringRevisionId === revision.id ? 'Restoring…' : 'Restore'}
                      </Button>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          ) : activePage ? (
            <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: 'var(--color-text-strong)', flex: 1 }}>
                  {activePage.label}
                </Typography>
                {activePage.sections && (
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DragIndicatorIcon sx={{ fontSize: 16 }} />
                    Connectors are shown automatically. Double-click a card to edit its text; use its corner to resize it, and right-click to lock its ratio or configure its line. Drag cards, connector bends, and the legend to adjust the layout.
                  </Typography>
                )}
                {activePage.key === 'overview' && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                      Double-click a project row to edit its cells. Right-click a table to change its region colour or aspect-ratio lock; right-click the legend (including the Launching Soon box) to customise its colour and label. Drag tables, the legend, or its header dividers to adjust the layout.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={() => {
                        setBackgroundUploadPage('overview');
                        backgroundInputRef.current?.click();
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload background
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addLaunchingSoonBox}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Launching Soon box
                    </Button>
                  </>
                )}
                {activePage.sections && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={() => {
                        setBackgroundUploadPage(activePage.key);
                        backgroundInputRef.current?.click();
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload background
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TuneIcon />}
                      onClick={() => setCardSettingsOpen(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Card fields
                    </Button>
                    <Button
                      size="small"
                      variant={multiSelectMode ? 'contained' : 'outlined'}
                      onClick={() => setMultiSelectMode((current) => !current)}
                      sx={{ textTransform: 'none' }}
                    >
                      {multiSelectMode ? 'Multi-select on' : 'Multi-select cards'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setMultiSelectMode(true);
                        setSelectAllRequests((current) => ({
                          ...current,
                          [activePage.key]: (current[activePage.key] ?? 0) + 1,
                        }));
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Multi Select All
                    </Button>
                  </>
                )}
                {activePage.sections && (
                  <Box
                    role="group"
                    aria-label="Connector line thickness"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      minHeight: 40,
                      px: 0.5,
                      border: '1px solid',
                      borderColor: 'rgba(var(--color-text-rgb), 0.24)',
                      borderRadius: 1,
                      flexWrap: 'nowrap',
                    }}
                  >
                    <LineWeightIcon fontSize="small" sx={{ color: 'var(--color-text-muted)', mx: 0.25 }} />
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap', mr: 0.25 }}>
                      Line thickness
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Decrease connector line thickness"
                      disabled={connectorThicknessForButtons <= MIN_CONNECTOR_STROKE_WIDTH}
                      onClick={() => adjustConnectorThickness(-0.5)}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      type="text"
                      value={connectorThicknessDraft}
                      onFocus={() => {
                        connectorThicknessEditingRef.current = true;
                        connectorThicknessEditingPageRef.current = activeTab;
                      }}
                      onChange={(event) => setConnectorThicknessDraft(event.target.value)}
                      onBlur={() => commitConnectorThickness(connectorThicknessDraft)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitConnectorThickness(connectorThicknessDraft);
                        }
                      }}
                      inputProps={{ inputMode: 'decimal', 'aria-label': 'Connector thickness in pixels' }}
                      sx={{
                        width: 54,
                        '& .MuiInputBase-input': { textAlign: 'center', py: 0.5, px: 0.25 },
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Increase connector line thickness"
                      disabled={connectorThicknessForButtons >= MAX_CONNECTOR_STROKE_WIDTH}
                      onClick={() => adjustConnectorThickness(0.5)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mr: 0.25 }}>px</Typography>
                  </Box>
                )}
                <input
                  ref={backgroundInputRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file && backgroundUploadPage) uploadBackground(backgroundUploadPage, file);
                    event.target.value = '';
                    setBackgroundUploadPage(null);
                  }}
                />
              </Box>
              {activePage.key === 'cover' && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => {
                      setBackgroundUploadPage('cover');
                      backgroundInputRef.current?.click();
                    }}
                    sx={{ textTransform: 'none', mb: 1.5 }}
                  >
                    Upload background
                  </Button>
                  <CoverPage backgroundImage={backgroundForPage('cover')} />
                </>
              )}
              {activePage.key === 'overview' && (
                <OverviewPage
                  items={overviewItems}
                  layout={layouts.overview || {}}
                  asAtDate={asAtDate}
                  editable
                  onItemChange={updateOverviewItem}
                  onTableMove={(key, x, y) => moveCard('overview', key, x, y)}
                  onTableResize={(key, width, height, x, y) => resizeCard('overview', key, width, height, x, y)}
                  onColumnWidthsChange={resizeTableColumns}
                  onTableAspectLockChange={setTableAspectLocked}
                  onRegionColorChange={updateRegionColor}
                  onLegendChange={(x, y, width) => adjustLegend('overview', x, y, width)}
                  onLegendCaptionChange={updateLegendCaption}
                  onLaunchingSoonBoxChange={updateLaunchingSoonBox}
                  onLaunchingSoonBoxDelete={deleteLaunchingSoonBox}
                  onLaunchingSoonColorChange={updateLaunchingSoonColor}
                  onLaunchingSoonTextChange={updateLaunchingSoonText}
                  backgroundImage={backgroundForPage('overview')}
                />
              )}
              {activePage.sections && (
                <MapPage
                  key={activePage.key}
                  page={activePage}
                  items={[...overviewItems, ...downstreamAdditions]}
                  markerReferenceItems={overviewItems}
                  itemOverrides={pageItemOverrides[activePage.key] || {}}
                  regionColorLayout={layouts.overview || {}}
                  layout={layouts[activePage.key] || {}}
                  asAtDate={asAtDate}
                  editable
                  onCardMove={(id, x, y) => moveCard(activePage.key, id, x, y)}
                  onCardDrop={(id, x, y) => moveCard(activePage.key, id, x, y)}
                  onCardResize={(id, width, height, x, y) => resizeCard(activePage.key, id, width, height, x, y)}
                  onMarkerMove={(markerKey, x, y) => moveMarker(activePage.key, markerKey, x, y)}
                  onMarkerLineLayoutChange={(markerKey, patch) => updateMarkerLineLayout(activePage.key, markerKey, patch)}
                  onMarkerBend={(markerKey, index, x, y) => updateMarkerBend(activePage.key, markerKey, index, x, y)}
                  onGeneratedCardPositions={(positions) => persistGeneratedCardPositions(activePage.key, positions)}
                  onLineBend={(id, x, y) => bendLine(activePage.key, id, x, y)}
                  onLineLayoutChange={(id, patch) => updateLineLayout(activePage.key, id, patch)}
                  onLegendChange={(x, y, width) => adjustLegend(activePage.key, x, y, width)}
                  onPageItemChange={(item) => updatePageItem(activePage.key, item)}
                  onCardDisplayChange={(settings, targetItemIds) => updateCardDisplay(activePage.key, settings, targetItemIds)}
                  routingGeneration={routingGenerations[activePage.key] ?? 0}
                  cachedRoutes={connectorRouteCache[activePage.key]}
                  connectorStrokeWidth={activeConnectorThickness}
                  cardSettingsOpen={cardSettingsOpen}
                  onCardSettingsClose={() => setCardSettingsOpen(false)}
                  backgroundImage={backgroundForPage(activePage.key)}
                  multiSelectMode={multiSelectMode}
                  selectAllRequest={selectAllRequests[activePage.key] ?? 0}
                  renderCards
                  renderConnectors
                />
              )}
            </Box>
          ) : null}
          </Box>
        </Box>
      </Box>

      {(exporting || publishing) && (
        <Box
          ref={exportRootRef}
          sx={{ position: 'fixed', left: -20000, top: 0, width: 1600, zIndex: -1, pointerEvents: 'none' }}
        >
          <CoverPage fixed backgroundImage={backgroundForExportPage('cover')} />
          <OverviewPage
            items={overviewItems}
            layout={layouts.overview || {}}
            asAtDate={asAtDate}
            fixed
            renderMode="print"
            backgroundImage={backgroundForExportPage('overview')}
          />
          {PAGES.filter((page) => page.sections).map((page) => (
            <MapPage
              key={page.key}
              page={page}
              items={[...overviewItems, ...downstreamAdditions]}
              markerReferenceItems={overviewItems}
              itemOverrides={pageItemOverrides[page.key] || {}}
              regionColorLayout={layouts.overview || {}}
              layout={layouts[page.key] || {}}
              asAtDate={asAtDate}
              fixed
              renderMode="print"
              routingGeneration={routingGenerations[page.key] ?? 0}
              cachedRoutes={connectorRouteCache[page.key]}
              connectorStrokeWidth={normalizeConnectorStrokeWidth(Number(layouts[page.key]?.['__connector-settings']?.connectorStrokeWidth))}
              onRoutingComplete={handleRoutingComplete}
              renderCards
              renderConnectors
              backgroundImage={backgroundForExportPage(page.key)}
            />
          ))}
        </Box>
      )}

      {/* Export progress */}
      <Dialog open={exporting} maxWidth="xs" fullWidth>
        <DialogContent sx={{ py: 4, textAlign: 'center' }}>
          <PictureAsPdfIcon sx={{ fontSize: 38, color: 'var(--color-accent)', mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
            Exporting PDF
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 2.5 }}>
            {exportStage === 'backgrounds'
              ? (exportProgress.total ? `Preparing background images (${exportProgress.done} of ${exportProgress.total})…` : 'Preparing background images…')
              : exportStage === 'layout'
              ? 'Preparing print layout…'
              : exportStage === 'finalizing'
              ? 'Finalizing PDF…'
              : `Rendering page ${Math.min(exportProgress.done + 1, exportProgress.total)} of ${exportProgress.total}…`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress.total ? (exportProgress.done / exportProgress.total) * 100 : 0}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={publishing} maxWidth="xs" fullWidth>
        <DialogContent sx={{ py: 4, textAlign: 'center' }}>
          <UploadFileIcon sx={{ fontSize: 38, color: 'var(--color-accent)', mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
            Preparing Residential image
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 1 }}>
            Rendering the fixed deck page, uploading it to Firebase, and creating a Showcase draft…
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 1 }}>
            {publishProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={publishProgress}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
